import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router, Params } from '@angular/router';
import * as _ from 'lodash';
import { map } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { DateRange } from 'src/app/components/company/models/new/dateRange';
import { Order, OrderFilters, OrderStatus, OrderType, OrderTypeEnum } from 'src/app/components/company/models/new/order';
import { Status } from 'src/app/components/company/models/new/status';
import { OrderMapper } from 'src/app/components/company/services/order-mapper';
import { ListModalComponent } from 'src/app/components/company/components/company-style-list/list-modal/list-modal.component';
import { OrderQueries } from 'src/app/shared/types/get-query';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { DateFilterListComponent } from 'src/app/shared/components/date-filter-list/date-filter-list.component';
import { FilterListComponent } from 'src/app/shared/components/filter-list/filter-list.component';
import { IdsFilterListComponent } from 'src/app/shared/components/ids-filter-list/ids-filter-list.component';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { OrderStatusFilterService } from 'src/app/services/filters/order-status-filter.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { Paginable } from 'src/app/shared/types/paginable';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { OrderTypeFilterService } from 'src/app/services/filters/order-type-filter.service';

@Component({
	selector: 'bt-photographer-order-list',
	standalone: false,
	templateUrl: './photographer-order-list.html',
	styleUrl: './photographer-order-list.scss'
})
export class PhotographerOrderList {
	readonly #destroyRef = inject(DestroyRef);

	orders: Paginable<Order> = new Paginable<Order>();
	orderFilters = new OrderFilters();
	orderStatus = OrderStatus;
	loading: boolean = false;

	constructor(
		private graphQlService: GraphQLService,
		private dialog: MatDialog,
		private appService: AppService,
		private route: ActivatedRoute,
		private utilitiesService: UtilitiesService,

		private orderStatusFilterService: OrderStatusFilterService,
		private orderTypeFilterService: OrderTypeFilterService,
		private orderMapper: OrderMapper,
		private router: Router
	) {}

	ngOnInit(): void {
		this.getOrders();
	}

	prepareFilterOptions(params: Params) {
		//questi chiamate servono per popolare il nome delle tendine di parametri in input tramite url

		if (params['statuses'])
			this.orderStatusFilterService.searchById(params['statuses']).subscribe((res: Status[]) => {
				this.orderFilters.statuses?.forEach((status) => {
					status.name = res.find((s: Status) => s.id == status.id)?.name;
				});
			});

		if (params['orderTypes'])
			this.orderTypeFilterService.searchById(params['orderTypes']).subscribe((res: OrderType[]) => {
				this.orderFilters.orderTypes?.forEach((orderType) => {
					orderType.name = res.find((item: OrderType) => item.id == orderType.id)?.name;
				});
			});
	}

	getOrders() {
		this.loading = true;
		this.route.queryParams.subscribe((params: Params) => {
			this.orderFilters = this.orderMapper.urlToFilters(params);

			//metodo che popola le label dei filtri in base agli id passati da url,
			this.prepareFilterOptions(params);

			this.orders.currentPage = this.orderFilters.page || 1;
			this.orders.size = this.orderFilters.size || 2;

			const pagination = this.orders.buildQuery(this.orders.currentPage);

			this.graphQlService
				.getData<any>(OrderQueries.GET_ORDERS, { where: this.orderMapper.toWhereClausePhotographer(this.orderFilters), ...pagination })
				.pipe(
					map((res: any) => {
						const orders = res.data.orders;

						const paginable = new Paginable<Selectable<Order>>({
							items: orders.nodes.map((node: any) => ({
								...this.orderMapper.toOrderSearch(node),
								selected: false
							}))
						});

						paginable.totalCount = orders.totalCount;

						paginable.currentPage = this.orders.currentPage;
						paginable.size = this.orders.size;

						return paginable;
					})
				)
				.subscribe({
					next: (data: Paginable<Selectable<Order>>) => {
						this.orders = data;
						this.loading = false;
					},
					error: (err) => {
						this.utilitiesService.showSnackbar('Error fetching orders', SnackbarType.Error);
						this.loading = false;
						console.error(err);
					}
				});
		});
	}

	pageEvent(event: { pageNumber: number; pageSize: number }) {
		this.orders.currentPage = event.pageNumber;
		this.orders.size = event.pageSize;

		this.orderFilters.page = event.pageNumber;
		this.orderFilters.size = event.pageSize;

		this.refreshNavigation();
	}

	refreshNavigation() {
		this.loading = true;

		this.router.navigate([location.pathname], {
			queryParams: this.orderMapper.filtersToUrl(this.orderFilters)
		});
	}

	cleanFilter(filter: string) {
		switch (filter) {
			case 'ids':
				this.orderFilters.ids = undefined;
				break;
			case 'basicNetIds':
				this.orderFilters.basicNetIds = undefined;
				break;
			case 'photographers':
				this.orderFilters.photographers = undefined;
				break;
			case 'dueDate':
				this.orderFilters.dueDate = undefined;
				break;
			case 'creationDate':
				this.orderFilters.creationDate = undefined;
				break;
			case 'completionDate':
				this.orderFilters.completionDate = undefined;
				break;
			case 'statuses':
				this.orderFilters.statuses = undefined;
				break;
			case 'orderTypes':
				this.orderFilters.orderTypes = undefined;
				break;
		}

		this.orderFilters.page = 1;

		this.refreshNavigation();
	}

	openDialogIdsFilter() {
		const dialog = this.dialog
			.open(IdsFilterListComponent, {
				data: { title: 'Order id', selected: this.orderFilters.ids }
			})
			.afterClosed()
			.subscribe({
				next: (res) => {
					if (res) {
						this.orderFilters.ids = res;
						this.refreshNavigation();
					}
				}
			});
	}

	openDialogBasicNetIdsFilter() {
		this.dialog
			.open(IdsFilterListComponent, {
				data: { title: 'BasicNet ID', selected: this.orderFilters.basicNetIds }
			})
			.afterClosed()
			.subscribe({
				next: (res) => {
					if (res) {
						this.orderFilters.basicNetIds = res;
						this.orderFilters.page = 1;
						this.refreshNavigation();
					}
				}
			});
	}

	openDueDateFilterModal() {
		this.dialog
			.open(DateFilterListComponent, {
				data: {
					title: 'Due Date',
					selected: _.cloneDeep(this.orderFilters.dueDate)
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: DateRange) => {
					if (res) {
						this.orderFilters.dueDate = res;
						this.refreshNavigation();
					}
				}
			});
	}

	openCreationDateFilterModal() {
		this.dialog
			.open(DateFilterListComponent, {
				data: {
					title: 'Creation Date',
					selected: _.cloneDeep(this.orderFilters.creationDate)
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: DateRange) => {
					if (res) {
						this.orderFilters.creationDate = res;
						this.refreshNavigation();
					}
				}
			});
	}

	openCompletionDateFilterModal() {
		this.dialog
			.open(DateFilterListComponent, {
				data: {
					title: 'Completion Date',
					selected: _.cloneDeep(this.orderFilters.completionDate)
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: DateRange) => {
					if (res) {
						this.orderFilters.completionDate = res;
						this.refreshNavigation();
					}
				}
			});
	}

	openStatusFilterModal(): void {
		this.dialog
			.open(FilterListComponent<Status>, {
				data: {
					title: 'Statuses',

					selected: this.orderFilters.statuses
						? this.orderFilters.statuses.map((el) => ({
								...el,
								displayId: el.id,
								displayName: el.name,
								selected: true
							}))
						: [],
					filterService: this.orderStatusFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: Array<Selectable<Status>>) => {
					if (res) {
						this.orderFilters.statuses = res.map((el: any) => new Status({ id: el.displayId, name: el.displayName }));
						this.refreshNavigation();
					}
				}
			});
	}

	openOrderTypeFilterModal(): void {
		this.dialog
			.open(FilterListComponent<OrderType>, {
				data: {
					title: 'Order Types',
					selected: this.orderFilters.orderTypes
						? this.orderFilters.orderTypes.map((el) => ({
								...el,
								displayId: el.id,
								displayName: el.name,
								selected: true,
								disabled: false
							}))
						: [],
					filterService: this.orderTypeFilterService
				},
				width: '400px'
			})
			.afterClosed()
			.subscribe({
				next: (res: Array<Selectable<OrderType>>) => {
					if (res) {
						this.orderFilters.orderTypes = res.map((el) => new OrderType({ id: el.id as OrderTypeEnum, name: el.name }));
						this.refreshNavigation();
					}
				}
			});
	}

	openOrderedSkusModal(orderId: number): void {
		this.graphQlService
			.getData<any>(OrderQueries.GET_ORDERED_SKUS, { orderId })
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (data: any) => {
					this.openListModal('Ordered SKUs', (data.data.orderedSkus as string[]) || []);
				},
				error: (err) => {
					this.utilitiesService.showSnackbar('Error fetching ordered SKUs', SnackbarType.Error);
					console.error(err);
				}
			});
	}

	openShippedSkusModal(orderId: number): void {
		this.graphQlService
			.getData<any>(OrderQueries.GET_SHIPPED_SKUS, { orderId })
			.pipe(takeUntilDestroyed(this.#destroyRef))
			.subscribe({
				next: (data: any) => {
					this.openListModal('Shipped SKUs', (data.data.shippedSkus as string[]) || []);
				},
				error: (err) => {
					this.utilitiesService.showSnackbar('Error fetching shipped SKUs', SnackbarType.Error);
					console.error(err);
				}
			});
	}

	openListModal(title: string, list: Array<string>): void {
		this.dialog.open(ListModalComponent, {
			data: {
				title,
				list
			}
		});
	}
}
